const DEFAULT_RULES = Object.freeze({
  maxLinesPerGame: 9,
  minGapPlies: 2,
  criticalPriority: 90,
  eventChances: {},
});
const OUTCOME_HISTORY_KEY = "brawnyai-chess:outcomes:v2";
const OUTCOME_HISTORY_LIMIT = 5;

export class OpponentPerformanceDirector {
  constructor(pack, options = {}) {
    this.pack = pack;
    this.random = options.random ?? Math.random;
    this.now = options.now ?? (() => new Date());
    this.storage = options.storage ?? safeLocalStorage();
    this.outcomeHistory = loadOutcomeHistory(this.storage);
    this.reset();
  }

  reset() {
    this.spokenIds = new Set();
    this.lastEventPly = new Map();
    this.lastSpokenPly = -Infinity;
    this.lineCount = 0;
  }

  sessionEvent() {
    return `session_${this.dayPeriod()}`;
  }

  dayPeriod() {
    const hour = this.now().getHours();
    if (hour < 6 || hour >= 23) {
      return "late_night";
    }
    if (hour < 12) {
      return "morning";
    }
    if (hour < 18) {
      return "afternoon";
    }
    return "evening";
  }

  line(event, opponentId, context = {}, options = {}) {
    const opponent = this.pack?.opponents?.[opponentId];
    const rules = { ...DEFAULT_RULES, ...this.pack?.director };
    const ply = Number(context.ply ?? 0);
    const critical =
      options.force || Number(options.priority ?? 0) >= rules.criticalPriority;
    if (!critical && this.lineCount >= rules.maxLinesPerGame) {
      return null;
    }
    if (!critical && ply - this.lastSpokenPly < rules.minGapPlies) {
      return null;
    }

    const chance = Number(rules.eventChances?.[event] ?? 1);
    if (!options.force && this.random() > chance) {
      return null;
    }

    const source = opponent?.lines?.length
      ? opponent.lines
      : (this.pack?.fallback ?? []);
    let candidates = source.filter((item) => {
      if (item.event !== event) {
        return false;
      }
      if (item.oncePerGame && this.spokenIds.has(item.id)) {
        return false;
      }
      if (this.spokenIds.has(item.id) && source.length > 1) {
        return false;
      }
      if (!matches(item.moods, context.mood)) {
        return false;
      }
      if (!matches(item.phases, context.phase)) {
        return false;
      }
      if (!matches(item.motifs, context.motif)) {
        return false;
      }
      if (!matches(item.periods, context.period)) {
        return false;
      }
      const lastEvent = this.lastEventPly.get(event) ?? -Infinity;
      return ply - lastEvent >= Number(item.cooldownPlies ?? 0);
    });
    if (event.startsWith("outcome_")) {
      const historyKey = outcomeHistoryKey(opponentId, event);
      const recent = this.outcomeHistory[historyKey] ?? [];
      const fresh = candidates.filter((item) => !recent.includes(item.id));
      if (fresh.length) {
        candidates = fresh;
      }
    }
    if (context.motif) {
      const motifSpecific = candidates.filter((item) =>
        item.motifs?.includes(context.motif),
      );
      if (motifSpecific.length) {
        candidates = motifSpecific;
      }
    }
    if (!candidates.length) {
      return null;
    }

    const selected = weightedChoice(candidates, this.random);
    this.spokenIds.add(selected.id);
    this.lastEventPly.set(event, ply);
    this.lastSpokenPly = ply;
    this.lineCount += 1;
    if (event.startsWith("outcome_")) {
      const historyKey = outcomeHistoryKey(opponentId, event);
      const recent = this.outcomeHistory[historyKey] ?? [];
      this.outcomeHistory[historyKey] = [selected.id, ...recent]
        .filter((id, index, items) => items.indexOf(id) === index)
        .slice(0, OUTCOME_HISTORY_LIMIT);
      saveOutcomeHistory(this.storage, this.outcomeHistory);
    }
    return selected.text;
  }
}

function outcomeHistoryKey(opponentId, event) {
  return `${opponentId}:${event}`;
}

export function phaseFor(view) {
  const pieces = view.board.filter(Boolean).length;
  const ply = view.history.length;
  if (pieces <= 10 || ply >= 48) {
    return "endgame";
  }
  if (ply < 16) {
    return "opening";
  }
  return "middlegame";
}

export function moodForHumanEvaluation(humanEvaluation) {
  if (!Number.isFinite(humanEvaluation)) {
    return "focused";
  }
  if (humanEvaluation <= -300) {
    return "confident";
  }
  if (humanEvaluation <= -90) {
    return "pressing";
  }
  if (humanEvaluation >= 300) {
    return "rattled";
  }
  if (humanEvaluation >= 90) {
    return "challenged";
  }
  return "focused";
}

export function normalizeEngineScore(score, sideToMove, humanSide) {
  if (!score) {
    return null;
  }
  const raw =
    score.type === "mate"
      ? Math.sign(score.value || 1) *
        (100_000 - Math.min(99, Math.abs(score.value)))
      : score.value;
  return sideToMove === humanSide ? raw : -raw;
}

export function classifyMoveQuality(before, after) {
  if (!Number.isFinite(before) || !Number.isFinite(after)) {
    return null;
  }
  const loss = Math.max(0, before - after);
  if (loss <= 12) {
    return { event: "quality_best", loss };
  }
  if (loss <= 35) {
    return { event: "quality_excellent", loss };
  }
  if (loss <= 75) {
    return { event: "quality_solid", loss };
  }
  if (loss <= 130) {
    return { event: "quality_inaccuracy", loss };
  }
  if (loss <= 260) {
    return { event: "quality_mistake", loss };
  }
  return { event: "quality_blunder", loss };
}

export function tacticalMotif(session, analysis) {
  const uci = analysis?.info?.pv?.[0] ?? analysis?.move;
  if (!/^[a-h][1-8][a-h][1-8][qrbn]?$/.test(uci ?? "")) {
    return "position";
  }
  const reply = session
    .legalMoves(uci.slice(0, 2))
    .find(
      (move) =>
        move.to === uci.slice(2, 4) && (!uci[4] || move.promotion === uci[4]),
    );
  if (!reply) {
    return "position";
  }
  if (reply.san.includes("#") || reply.san.includes("+")) {
    return "king_safety";
  }
  if (reply.captured) {
    return "hanging_piece";
  }
  return "position";
}

function matches(allowed, actual) {
  return !allowed?.length || allowed.includes(actual);
}

function weightedChoice(items, random) {
  const total = items.reduce((sum, item) => sum + Number(item.weight ?? 1), 0);
  let cursor = random() * total;
  for (const item of items) {
    cursor -= Number(item.weight ?? 1);
    if (cursor <= 0) {
      return item;
    }
  }
  return items.at(-1);
}

function safeLocalStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function loadOutcomeHistory(storage) {
  try {
    const value = JSON.parse(storage?.getItem(OUTCOME_HISTORY_KEY) ?? "{}");
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

function saveOutcomeHistory(storage, history) {
  try {
    storage?.setItem(OUTCOME_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // Dialogue still works when storage is unavailable or full.
  }
}
